import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import StoreSubnav from "@/components/store/StoreSubnav";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { formatPrice } from "@/store/storeCart";
import RefundRequestButton from "@/components/store/RefundRequestButton";

type OrderItem = {
  id: string;
  product_title: string;
  variant_title: string | null;
  quantity: number;
  unit_price_cents: number;
  subtotal_cents: number;
  digital_delivery_url: string | null;
};
type Order = {
  id: string;
  order_number: string;
  status: string;
  subtotal_cents: number;
  tax_cents: number;
  shipping_cents: number;
  total_cents: number;
  created_at: string;
  fulfilled_at: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
  shipping_address: any;
  vendor: { display_name: string; slug: string; support_email: string | null } | null;
  items: OrderItem[];
};

const StoreOrderDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { user, loading: authLoading } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("store_orders")
        .select(
          `id, order_number, status, subtotal_cents, tax_cents, shipping_cents, total_cents,
           created_at, fulfilled_at, shipped_at, delivered_at, tracking_number, tracking_url,
           shipping_address,
           vendor:store_vendors_public!vendor_id(display_name, slug, support_email),
           items:store_order_items(id, product_title, variant_title, quantity, unit_price_cents, subtotal_cents, digital_delivery_url)`,
        )
        .eq("id", id)
        .eq("buyer_id", user.id)
        .maybeSingle();
      if (cancelled) return;
      setOrder(data as unknown as Order);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, id]);

  if (!authLoading && !user) {
    return <Navigate to={`/auth?redirect=/store/orders/${id}`} replace />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <StoreSubnav />
      <main className="flex-1 mx-auto w-full max-w-3xl px-6 py-8">
        <Link to="/store/orders" className="text-sm text-muted-foreground hover:underline">
          ← All orders
        </Link>

        {loading ? (
          <Skeleton className="h-64 mt-6" />
        ) : !order ? (
          <Card className="p-12 text-center mt-6">
            <p className="text-muted-foreground">Order not found.</p>
          </Card>
        ) : (
          <>
            <div className="mt-4 flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold">{order.order_number}</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Placed {new Date(order.created_at).toLocaleString()}
                </p>
                {order.vendor && (
                  <Link
                    to={`/store/vendor/${order.vendor.slug}`}
                    className="text-sm underline mt-1 inline-block"
                  >
                    {order.vendor.display_name}
                  </Link>
                )}
              </div>
              <Badge>{order.status.replace("_", " ")}</Badge>
            </div>

            <Card className="mt-6 p-4 divide-y divide-border">
              {order.items.map((it) => (
                <div key={it.id} className="py-3 flex justify-between gap-4 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{it.product_title}</p>
                    {it.variant_title && (
                      <p className="text-sm text-muted-foreground">{it.variant_title}</p>
                    )}
                    <p className="text-sm text-muted-foreground">Qty {it.quantity}</p>
                    {it.digital_delivery_url && (
                      <a
                        href={it.digital_delivery_url}
                        className="text-sm text-primary underline mt-1 inline-block"
                      >
                        Download
                      </a>
                    )}
                  </div>
                  <p className="font-medium whitespace-nowrap">
                    {formatPrice(it.subtotal_cents)}
                  </p>
                </div>
              ))}
            </Card>

            <Card className="mt-4 p-4">
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Subtotal</dt>
                  <dd>{formatPrice(order.subtotal_cents)}</dd>
                </div>
                {order.tax_cents > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Tax</dt>
                    <dd>{formatPrice(order.tax_cents)}</dd>
                  </div>
                )}
                {order.shipping_cents > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Shipping</dt>
                    <dd>{formatPrice(order.shipping_cents)}</dd>
                  </div>
                )}
                <div className="flex justify-between font-semibold pt-2 border-t border-border">
                  <dt>Total</dt>
                  <dd>{formatPrice(order.total_cents)}</dd>
                </div>
              </dl>
            </Card>

            {(order.tracking_number || order.shipped_at) && (
              <Card className="mt-4 p-4">
                <h3 className="font-semibold">Shipping</h3>
                {order.shipped_at && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Shipped {new Date(order.shipped_at).toLocaleDateString()}
                  </p>
                )}
                {order.tracking_number && (
                  <p className="text-sm mt-1">
                    Tracking: {order.tracking_url ? (
                      <a href={order.tracking_url} className="text-primary underline">
                        {order.tracking_number}
                      </a>
                    ) : (
                      order.tracking_number
                    )}
                  </p>
                )}
              </Card>
            )}

            <Card className="mt-4 p-4 bg-muted/30">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-sm">Need help?</h3>
                  <p className="text-xs text-muted-foreground mt-1 max-w-md">
                    Refund window: 14 days from delivery. Requests under $25 fast-track.
                    Contact{" "}
                    {order.vendor?.support_email ? (
                      <a href={`mailto:${order.vendor.support_email}`} className="underline">
                        the vendor
                      </a>
                    ) : (
                      "the vendor"
                    )}{" "}
                    first, then{" "}
                    <a href="mailto:support@wavlynq.com" className="underline">
                      Wavlynq support
                    </a>{" "}
                    if unresolved.
                  </p>
                </div>
                <RefundRequestButton
                  order={{
                    id: order.id,
                    order_number: order.order_number,
                    status: order.status,
                    total_cents: order.total_cents,
                    delivered_at: order.delivered_at,
                    shipped_at: order.shipped_at,
                    created_at: order.created_at,
                  }}
                />
              </div>
            </Card>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
};

export default StoreOrderDetailPage;
